import { type ReactNode } from 'react'
import { Float } from '@headlessui-float/react'
import { Menu, MenuButton, MenuItems } from '@headlessui/react'
import type { LocaleEnum } from '@systemeio/ui-frontend'
import { localeNames } from '@systemeio/ui-frontend'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import { HoverOverlay, HoverOverlayArrow } from 'shared/components/hover-overlay'
import { useHeadlessUiAnchor } from 'shared/feature-flags/use-headless-ui-anchor'
import { useFloatHover } from 'shared/hooks/use-float-hover'
import { useLanguageSwitcher } from 'shared/hooks/use-language-switcher'
import { getCalculatedLanguage } from 'shared/utils/get-calculated-language'
import useUser from '../hooks/use-user'
import ChevronDownIcon from '../icons/chevron-down-icon'

export function PublicDashboardLanguageSwitcher(): ReactNode {
  const { i18n } = useTranslation()
  const { user } = useUser()
  const { show, delayClose, delayOpen } = useFloatHover()
  const { handleChangeLanguage, isChangingLanguage } = useLanguageSwitcher()
  if (!i18n) {
    return <div>loading...</div>
  }

  return user ? (
    <HoverOverlay
      show={show}
      placement="bottom"
      gap={8}
      className={'flex h-full items-center'}
      floatProps={{
        offset: -5,
        arrow: true,
        enter: 'transition duration-100 ease-out',
        enterFrom: 'opacity-0',
        enterTo: 'opacity-100',
        leave: 'transition duration-75 ease-in',
        leaveFrom: 'opacity-100',
        leaveTo: 'opacity-0',
      }}
    >
      <button
        data-testid="public-dashboard-language-switcher-trigger"
        onMouseEnter={delayOpen}
        onMouseLeave={delayClose}
        className="flex h-full items-center justify-around gap-2.5 border-b-4 border-transparent focus:outline-hidden focus-visible:border-brand-primary"
      >
        <div
          className={twMerge(
            'w-5 text-sm font-bold text-background-secondary/90',
            isChangingLanguage && 'shimmer-loading',
          )}
        >
          {i18n && i18n.language ? getCalculatedLanguage(i18n.language) : ''}
        </div>
        <ChevronDownIcon />
      </button>

      <div
        data-testid="public-dashboard-language-switcher-panel"
        onMouseEnter={delayOpen}
        onMouseLeave={delayClose}
        className="flex flex-col rounded-lg bg-background-secondary py-4 text-sm shadow-lg"
      >
        <HoverOverlayArrow
          className={`absolute left-0 size-0 -translate-y-3.5 border-10 border-x-transparent border-t-transparent border-b-background-secondary`}
        />
        <ul>
          {Object.keys(localeNames)
            .filter(dashboardLocale => dashboardLocale !== i18n.language)
            .map(dashboardLocale => (
              <li key={dashboardLocale}>
                <button
                  className="w-full cursor-pointer px-7 py-1 text-start text-text-tertiary hover:text-brand-primary focus:outline-hidden focus-visible:text-brand-primary"
                  onClick={async () =>
                    handleChangeLanguage(dashboardLocale as LocaleEnum, { forceRouterUpdate: true })
                  }
                  type="button"
                >
                  {localeNames[dashboardLocale as LocaleEnum]}
                </button>
              </li>
            ))}
        </ul>
      </div>
    </HoverOverlay>
  ) : (
    <div className="h-6 w-11 animate-pulse rounded-xs bg-skeleton-primary" />
  )
}

export function MobilePublicLanguageSwitcher(): ReactNode {
  const { i18n } = useTranslation()
  const { user } = useUser()
  const { handleChangeLanguage, isChangingLanguage } = useLanguageSwitcher()
  const useNativeAnchor = useHeadlessUiAnchor()

  if (!i18n) {
    return <div>loading...</div>
  }

  const trigger = (
    <MenuButton
      data-testid="public-dashboard-mobile-language-switcher-trigger"
      className="relative flex items-center justify-between gap-2 px-4 py-2"
    >
      <span
        className={twMerge(
          'flex-1 text-start text-text-secondary capitalize',
          isChangingLanguage && 'shimmer-loading',
        )}
      >
        {user?.dashboardLocale}
      </span>
      <ChevronDownIcon className="size-3 rotate-180" aria-hidden="true" />
    </MenuButton>
  )

  const items = (
    <MenuItems
      {...(useNativeAnchor
        ? { portal: true, anchor: { to: 'top end' as const, gap: 8 } }
        : { static: true })}
      className="divide-y divide-fill-primary rounded-md bg-background-secondary shadow-lg ring-1 ring-black/5 focus:outline-hidden"
    >
      <div className="py-1">
        {Object.keys(localeNames)
          .filter(dashboardLocale => dashboardLocale !== i18n.language)
          .map(dashboardLocale => (
            <MenuButton
              className="my-2 px-4 py-1 text-text-secondary"
              onClick={async () =>
                handleChangeLanguage(dashboardLocale as LocaleEnum, { forceRouterUpdate: true })
              }
              key={dashboardLocale}
            >
              {localeNames[dashboardLocale as LocaleEnum]}
            </MenuButton>
          ))}
        {Object.keys(localeNames).includes(i18n.language) && (
          <MenuButton
            className="my-2 flex w-full items-center px-4 py-1 text-brand-primary"
            onClick={async () =>
              handleChangeLanguage(i18n.language as LocaleEnum, { forceRouterUpdate: true })
            }
          >
            {localeNames[i18n.language as LocaleEnum]}
            <ChevronDownIcon className="ml-3 size-3 rotate-180" aria-hidden="true" />
          </MenuButton>
        )}
      </div>
    </MenuItems>
  )

  return (
    <Menu>
      {useNativeAnchor ? (
        <div className="relative">
          {trigger}
          {items}
        </div>
      ) : (
        <Float
          as="div"
          className="relative"
          placement="top-end"
          flip
          offset={-25}
          enter="transition duration-300 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition duration-175 ease-in"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          {trigger}
          {items}
        </Float>
      )}
    </Menu>
  )
}
